function getProductGrid() {
  return document.querySelector('ul.product-grid');
}

function getPaginateNext() {
  return document.getElementById('paginateNext');
}

function getNextPageUrl() {
  const el = getPaginateNext();
  const url = el?.dataset.nextUrl;
  return url && url.length > 0 ? url : null;
}

function getLoadMoreBtn() {
  return document.querySelector('.load-more_btn');
}

function getLoadMoreSpinner() {
  return document.querySelector('.load-more_spinner');
}

function getCounter() {
  return document.querySelector('.load-more__counter');
}

function getSectionId() {
  const sectionEl = document.querySelector('.collection[data-id]');
  return sectionEl ? sectionEl.dataset.id : null;
}

function getCurrentPage() {
  const el = getPaginateNext();
  const fromAttr = parseInt(el?.dataset.currentPage, 10);
  if (!isNaN(fromAttr) && fromAttr > 0) return fromAttr;
  const fromUrl = parseInt(new URLSearchParams(window.location.search).get('page'), 10);
  return !isNaN(fromUrl) && fromUrl > 0 ? fromUrl : 1;
}

function buildSectionUrl(url) {
  const sectionId = getSectionId();
  if (!sectionId) return url;
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}section_id=${encodeURIComponent(sectionId)}`;
}

function buildPageUrl(pageNum) {
  const base = window.location.pathname;
  const params = new URLSearchParams(window.location.search);
  params.set('page', String(pageNum));
  return `${base}?${params.toString()}`;
}

// Extract grid items from an AJAX section response.
function extractCards(htmlString) {
  const doc = new DOMParser().parseFromString(htmlString, 'text/html');
  const responseGrid = doc.getElementById('product-grid');
  if (!responseGrid) return { cards: [], doc };
  return { cards: Array.from(responseGrid.querySelectorAll('.grid__item')), doc };
}

// Normalize a product link to just its pathname so tracking query strings
// (e.g. ?_pos=2 added by some Shopify apps) don't break de-duplication.
function productKey(card) {
  const link = card.querySelector('a[href*="/products/"]');
  if (!link) return null;
  try {
    return new URL(link.href, window.location.origin).pathname;
  } catch {
    return link.href;
  }
}

function getExistingKeys(grid) {
  const set = new Set();
  grid.querySelectorAll('.grid__item').forEach((item) => {
    const key = productKey(item);
    if (key) set.add(key);
  });
  return set;
}

function dedupeCards(cards, existingKeys) {
  return cards.filter((card) => {
    const key = productKey(card);
    if (!key) return true;
    if (existingKeys.has(key)) return false;
    existingKeys.add(key);
    return true;
  });
}

// Update counter text using the server-rendered template stored in
// data-template — keeps server-side i18n / pluralization intact.
// Clamps loaded to total so a stale grid (e.g. mid-filter-swap) can never
// produce nonsense like "32 of 19".
function updateCounter() {
  const counter = getCounter();
  if (!counter) return;
  const template = counter.dataset.template;
  if (!template) return;
  const grid = getProductGrid();
  const loaded = grid ? grid.querySelectorAll('.grid__item').length : 0;
  const total = parseInt(counter.dataset.total, 10);
  const display = isNaN(total) ? loaded : Math.min(loaded, total);
  counter.textContent = template.replace('__LOADED__', display);
}

async function fetchSection(pageUrl) {
  try {
    const res = await fetch(buildSectionUrl(pageUrl));
    if (!res.ok) {
      console.error('[Load More] Fetch failed:', res.status, pageUrl);
      return null;
    }
    return await res.text();
  } catch (error) {
    console.error('[Load More] Fetch error:', error, pageUrl);
    return null;
  }
}

async function loadMoreProducts() {
  const btn = getLoadMoreBtn();
  const spinner = getLoadMoreSpinner();
  if (!btn || !spinner) return;

  const nextUrl = getNextPageUrl();
  if (!nextUrl) return;

  btn.style.display = 'none';
  spinner.classList.remove('hidden');

  const html = await fetchSection(nextUrl);
  if (!html) {
    spinner.classList.add('hidden');
    btn.style.display = 'flex';
    return;
  }

  spinner.classList.add('hidden');

  const grid = getProductGrid();
  if (!grid) return;

  const { cards, doc } = extractCards(html);
  const existing = getExistingKeys(grid);
  const newCards = dedupeCards(cards, existing);

  newCards.forEach((card) => {
    grid.appendChild(card);
    executeInlineScripts(card);
  });

  // Update #paginateNext and button visibility from the response.
  const responsePaginateNext = doc.getElementById('paginateNext');
  const livePaginateNext = getPaginateNext();
  if (livePaginateNext) {
    livePaginateNext.dataset.nextUrl = responsePaginateNext?.dataset.nextUrl || '';
    livePaginateNext.dataset.currentPage = responsePaginateNext?.dataset.currentPage || '';
  }

  if (livePaginateNext && livePaginateNext.dataset.nextUrl) {
    btn.style.display = 'flex';
  } else {
    btn.remove();
    spinner.remove();
  }

  updateCounter();

  // Update browser URL to the page we just loaded. Extract page number from
  // the fetched URL (e.g. ?page=3) and use buildPageUrl to preserve filters.
  const fetchedPage = parseInt(new URL(nextUrl, window.location.origin).searchParams.get('page'), 10);
  if (!isNaN(fetchedPage) && fetchedPage > 0) {
    history.pushState({ page: fetchedPage }, '', buildPageUrl(fetchedPage));
  }

  reinitializeThirdPartyApps();
}

// Backfill must only run once per page load. reinitializeThirdPartyApps()
// dispatches a synthetic DOMContentLoaded for third-party scripts; without
// this guard, every Load More click would re-trigger backfill.
let backfillHasRun = false;

// On initial load, if the URL has ?page=N where N > 1, the server only
// rendered chunk N. Backfill pages 1..N-1 by fetching them in parallel and
// prepending the resulting cards so the user sees the cumulative grid.
async function backfillPreviousPages() {
  if (backfillHasRun) return;
  backfillHasRun = true;

  const currentPage = getCurrentPage();
  if (currentPage <= 1) return;

  const grid = getProductGrid();
  if (!grid) return;

  // If the grid already holds at least as many cards as the total (e.g. a
  // filtered set that's smaller than currentPage * per_page worth of products),
  // backfill would only fetch empty/wasteful pages. Skip it.
  const counter = getCounter();
  const total = counter ? parseInt(counter.dataset.total, 10) : NaN;
  const currentGridCount = grid.querySelectorAll('.grid__item').length;
  if (!isNaN(total) && currentGridCount >= total) {
    updateCounter();
    return;
  }

  const btn = getLoadMoreBtn();
  const wasButtonVisible = btn && btn.style.display !== 'none';
  if (btn) btn.style.display = 'none';

  // Fetch all previous pages in parallel.
  const fetchPromises = [];
  for (let p = 1; p < currentPage; p++) {
    fetchPromises.push(fetchSection(buildPageUrl(p)));
  }
  const responses = await Promise.all(fetchPromises);

  // Collect cards from each response, in page order (page 1 first).
  const backfillCards = [];
  for (const html of responses) {
    if (!html) continue;
    const { cards } = extractCards(html);
    backfillCards.push(...cards);
  }

  if (backfillCards.length === 0) {
    if (btn && wasButtonVisible) btn.style.display = 'flex';
    return;
  }

  const existing = getExistingKeys(grid);
  const cardsToPrepend = dedupeCards(backfillCards, existing);

  // Prepend cards in order: build a fragment in page order, then prepend.
  // documentFragment preserves child order on insertion.
  const fragment = document.createDocumentFragment();
  cardsToPrepend.forEach((card) => fragment.appendChild(card));
  grid.prepend(fragment);
  cardsToPrepend.forEach(executeInlineScripts);

  updateCounter();
  if (btn && wasButtonVisible) btn.style.display = 'flex';

  reinitializeThirdPartyApps();
}

// Scripts parsed by DOMParser have their "already started" flag set, so they
// don't run when inserted via appendChild. Clone-and-replace forces execution.
function executeInlineScripts(root) {
  root.querySelectorAll('script').forEach((oldScript) => {
    const newScript = document.createElement('script');
    for (const { name, value } of oldScript.attributes) {
      newScript.setAttribute(name, value);
    }
    newScript.textContent = oldScript.textContent;
    oldScript.parentNode.replaceChild(newScript, oldScript);
  });
}

function reinitializeThirdPartyApps() {
  const sectionEl = document.querySelector('.collection[data-id]');
  if (sectionEl) {
    document.dispatchEvent(
      new CustomEvent('shopify:section:load', {
        bubbles: true,
        detail: { sectionId: sectionEl.dataset.id },
      })
    );
  }
  document.dispatchEvent(new CustomEvent('shopify:page:change'));
  document.dispatchEvent(new Event('DOMContentLoaded'));

  const swatchScript = document.querySelector('script[src*="color-swatch-renderer"]');
  if (swatchScript) {
    const newScript = document.createElement('script');
    newScript.src = swatchScript.src;
    document.head.appendChild(newScript);
  }
}

// Browser back/forward: reload so the server renders the correct chunk and
// JS backfills any earlier pages.
window.addEventListener('popstate', () => {
  window.location.reload();
});

// Expose for inline onclick="loadMoreProducts()" in the section template.
window.loadMoreProducts = loadMoreProducts;

// Kick off backfill once the DOM is ready (script is loaded with defer, so
// DOM is parsed by the time this runs).
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', backfillPreviousPages);
} else {
  backfillPreviousPages();
}
